import React, { FC, useCallback, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import i18next, { changeLanguage } from "i18next";
import { useQueryClient } from "@tanstack/react-query";

import SidebarAccountPopover from "./SidebarAccountPopover";

import { useSidebarStateStore } from "@store/sidebar";
import { useAuth } from "@hooks/useAuth";
import { PLATFORM_LANGUAGES, PlatformLanguage } from "@constants";
import { DOMAIN_FORMS, useUpdateUserLanguage } from "@api";
import { useUserStore } from "@store/user";

interface SidebarAccountPopoverContainerProps {
  open: boolean;

  onClose(): void;

  anchorEl: React.RefObject<HTMLButtonElement>;
}

const SidebarAccountPopoverContainer: FC<SidebarAccountPopoverContainerProps> = ({
  open,
  onClose,
  anchorEl,
}) => {
  const navigate = useNavigate();
  const { setMobileDrawerOpen } = useSidebarStateStore();
  const { logout, refreshToken } = useAuth();
  const { user } = useUserStore();
  const { updateUserLanguageAsync } = useUpdateUserLanguage(user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();

  const [languagePopoverOpen, setLanguagePopoverOpen] = useState(false);

  const handleSettingsClick = (): void => {
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
    navigate("/account-settings");
  };

  const handleLanguageChanged = async (lng: PlatformLanguage): Promise<void> => {
    if (lng.code === (i18next.resolvedLanguage || i18next.language)) {
      return;
    }

    await changeLanguage(lng.code);
    await updateUserLanguageAsync(lng.id);
    await refreshToken();
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === DOMAIN_FORMS,
    });
  };

  const findLanguageName = useCallback(
    (code: string) =>
      PLATFORM_LANGUAGES.find((lng) => lng.code === code)?.name || code,
    [],
  );

  const handleLogout = (): void => {
    logout();
  };

  const handleAddFeaturesClick = (): void => {
    navigate("/account-settings#features");
  };

  return (
    <SidebarAccountPopover
      open={open}
      onClose={onClose}
      anchorEl={anchorEl}
      languagePopoverOpen={languagePopoverOpen}
      setLanguagePopoverOpen={setLanguagePopoverOpen}
      handleSettingsClick={handleSettingsClick}
      handleLanguageChanged={handleLanguageChanged}
      findLanguageName={findLanguageName}
      handleLogout={handleLogout}
      handleAddFeaturesClick={handleAddFeaturesClick}
      currentLanguage={i18next.resolvedLanguage || "en"}
    />
  );
};

export default SidebarAccountPopoverContainer;
